import { HttpErrorResponse } from '@angular/common/http';
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Input,
    OnDestroy,
    OnInit,
    Output,
} from '@angular/core';
import { UntypedFormBuilder, Validators } from '@angular/forms';
import { WorkshopCar } from '@impact/data';
import { forkJoin, of, Subject, throwError } from 'rxjs';
import { catchError, debounceTime, switchMap, takeUntil, tap } from 'rxjs/operators';

import { arrowRightIcon, searchIcon } from '../../../../icons';
import { WorkshopService } from '../../../core/workshop.service';
import { FormService } from '../../../form/form.service';
import { IIndicataResponse } from '../../../indicata/indicata.models';
import { IndicataService } from '../../../indicata/indicata.service';
import { IBooking } from '../../../models/book-workshop.models';
import { removeDiacritics } from '../../../utils/string.utils';

@Component({
    selector: 'app-book-workshop-car-info',
    template: `
        <section class="flow-step__header">
            <h1 class="h4">{{ 'book_workshop.car_information' | translate }}</h1>
            <div>{{ 'book_workshop.enter_car_registration_number' | translate }}</div>
        </section>
        <form class="book-workshop-stepper-content__form" [formGroup]="form" (ngSubmit)="onSubmit()">
            <section class="indicata__input-wrapper">
                <app-input
                    cssClasses="input__input-element--white-background"
                    controlName="regNumber"
                    [parentForm]="form"
                    [label]="'indicata.registration_number____input_name' | translate"
                    [asLicensePlate]="true"
                    [autoFocus]="true"
                ></app-input>

                <app-button buttonType="submit" buttonClass="button--primary button--has-spacing" [loading]="loadingCarData">
                    <i aria-hidden="true" class="button__icon">${searchIcon}</i>
                </app-button>
            </section>

            <p class="text-small center state state--info" *ngIf="hasError">
                {{ 'book_workshop.car_not_found' | translate }}
            </p>

            <p class="text-secondary">
                <ng-container *ngIf="carIndicataData">
                    {{ carIndicataData?.make?.summary }} {{ carIndicataData?.model?.summary }}
                    <span *ngIf="carIndicataData?.regDate?.summary">, {{ carIndicataData?.regDate?.summary }}</span>
                </ng-container>
                <ng-container *ngIf="makeCode && modelCode && !carIndicataData">
                    {{ 'book_workshop.license_plate_found_in_our_system' | translate }}
                </ng-container>
            </p>

            <ng-container *ngIf="showMileageIput">
                <div class="indicata__input-wrapper">
                    <app-input
                        controlName="mileage"
                        cssClasses="input__input-element--white-background"
                        [autoFocus]="true"
                        [inputMode]="'decimal'"
                        [label]="'book_workshop.mileage' | translate"
                        [parentForm]="form"
                    ></app-input>
                </div>
            </ng-container>
            <div class="book-workshop-flow__bottom">
                <div class="book-workshop-flow__bottom-content one-button-step">
                    <app-button
                        [title]="'book_workshop.continue' | translate"
                        [disabled]="loadingCarData || (showMileageIput && !form.controls['mileage'].value)"
                        buttonClass="book-workshop-stepper-content__continue-button button button--primary"
                        buttonType="submit">
                        <i aria-hidden="true" class="button__icon">${arrowRightIcon}</i>
                    </app-button>
                </div>
            </div>
        </form>
    `,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BookWorkshopCarInfoComponent implements OnInit, OnDestroy {
    @Input() booking: IBooking | null;
    @Output() regNumberChange = new EventEmitter<string>();
    @Output() nextStep = new EventEmitter<IBooking>();

    hasError: boolean;
    loadingCarData = false;
    loadingModels = false;
    showMileageIput = false;
    makeCode?: string;
    makeName?: string;
    modelCode?: string;
    modelName?: string;
    variantCode?: string;
    carIndicataData?: IIndicataResponse; // Car data, if fetched from Indicata.
    carDracarData?: WorkshopCar; // Car data, if fetched from Dracar.

    form = this.fb.group(
        {
            regNumber: ['', { validators: [Validators.required, Validators.maxLength(7)] }],
            mileage: ['', { validators: [Validators.required, Validators.pattern('^[0-9]*$')] }], // whole numbers only
        },
        {
            updateOn: 'change',
        }
    );

    get regNumberControl() {
        return this.form.get('regNumber');
    }

    private readonly unsubscribe = new Subject<void>();

    constructor(
        private fb: UntypedFormBuilder,
        private workshopService: WorkshopService,
        private cdr: ChangeDetectorRef,
        private indicataService: IndicataService
    ) {
        this.regNumberControl?.valueChanges
            .pipe(debounceTime(500), takeUntil(this.unsubscribe))
            .subscribe((regNumber) => this.regNumberChange.emit(regNumber));
    }

    ngOnInit(): void {
        if (this.booking) {
            this.makeCode = this.booking.makeCode;
            this.makeName = this.booking.makeName;
            this.modelCode = this.booking.modelCode;
            this.modelName = this.booking.modelName;
            this.variantCode = this.booking.variantCode;
            this.carDracarData = this.booking.carDracarData;
            this.carIndicataData = this.booking.carIndicataData;

            this.form.setValue(
                {
                    regNumber: this.booking.regNumber ?? '',
                    mileage: `${this.booking.mileage}` ?? '',
                },
                { emitEvent: false }
            );

            if (this.booking.mileage !== undefined) {
                this.showMileageIput = true;
            }
        }
    }

    ngOnDestroy(): void {
        this.unsubscribe.next();
        this.unsubscribe.complete();
    }

    resetForm() {
        this.makeCode = undefined;
        this.modelCode = undefined;
        this.variantCode = undefined;
        this.carIndicataData = undefined;
        this.hasError = false;
        this.showMileageIput = false;
        this.form?.controls['mileage'].setErrors(null);
        this.form?.controls['mileage'].setValue('');
    }

    updateCarData() {
        this.resetForm();
        this.loadingCarData = true;

        this.workshopService
            .getCarDetails(this.regNumberControl?.value)
            .pipe(takeUntil(this.unsubscribe))
            .subscribe(
                // On success:
                (carDracarData: WorkshopCar) => {
                    const { make, makeCode, model, modelCode, variantCode } = carDracarData;
                    this.handleDataFetchSuccess({ makeCode, makeName: make, modelCode, modelName: model, variantCode, carDracarData });
                },
                // No success:
                () => this.fetchMakeAndModelFromIndicata()
            );
    }

    fetchMakeAndModelFromIndicata() {
        let _indicataResponse: IIndicataResponse;
        let makeCode: string | null | undefined;
        let makeName: string | null | undefined;
        let modelCode: string | null | undefined;
        let modelName: string | null | undefined;

        forkJoin({ indicata: this.indicataService.independentIndicataFetch(this.regNumberControl?.value), makes: this.workshopService.getMakes() })
            .pipe(
                tap(({ indicata, makes }) => {
                    _indicataResponse = indicata;
                    const _make = makes.find(
                        ({ name }) => removeDiacritics(name?.toLowerCase()) === removeDiacritics(indicata.make?.summary?.toLowerCase())
                    );
                    makeCode = _make?.code;
                    makeName = _make?.name;
                }),
                switchMap(() => (makeCode ? this.workshopService.getModels(makeCode) : of([]))),
                catchError((error: HttpErrorResponse) => this.onGetCarDetailsError(error)),
                takeUntil(this.unsubscribe)
            )
            .subscribe((models) => {
                const _model = models.find(
                    ({ name }) => removeDiacritics(name?.toLowerCase()) === removeDiacritics(_indicataResponse.model?.summary?.toLowerCase())
                );
                modelCode = _model?.code;
                modelName = _model?.name;
                this.handleDataFetchSuccess({ makeCode, makeName, modelCode, modelName, carIndicataData: _indicataResponse });
            });
    }

    handleDataFetchSuccess({
        makeName,
        makeCode,
        modelCode,
        modelName,
        carIndicataData,
        carDracarData,
        variantCode,
    }: {
        carDracarData?: WorkshopCar;
        carIndicataData?: IIndicataResponse;
        makeCode?: string | null;
        makeName?: string | null;
        modelCode?: string | null;
        modelName?: string | null;
        variantCode?: string | null;
    }) {
        this.carDracarData = carDracarData;
        this.carIndicataData = carIndicataData;
        this.loadingCarData = false;
        const defaultCode = 'ZZ';
        this.makeCode = makeCode || defaultCode;
        this.modelCode = modelCode || defaultCode;
        this.makeName = makeName || undefined;
        this.modelName = modelName || undefined;
        this.variantCode = variantCode || 'ZZZZZZ';
        this.showMileageIput = true;
        this.cdr.markForCheck();
    }

    onSubmit() {
        const { regNumber, mileage: mileageString }: { [key: string]: string } = this.form?.value;

        const noCarData = !this.makeCode && !this.modelCode;
        const newLicensePlate =
            this.carIndicataData?.identifier?.toLowerCase() !== regNumber.toLowerCase() &&
            this.carDracarData?.registrationNumber?.toLowerCase() !== regNumber.toLowerCase() &&
            this.booking?.regNumber?.toLowerCase() !== regNumber.toLowerCase();
        const noLicensePlate = !this.carIndicataData?.identifier && !this.booking?.regNumber && !this.carDracarData?.registrationNumber;

        if (!newLicensePlate && this.showMileageIput && (this.form?.invalid || this.form?.disabled)) {
            FormService.markControlsAsTouched(this.form);
            return;
        }

        if (noCarData || newLicensePlate || noLicensePlate) {
            if (this.carIndicataData?.identifier?.toLowerCase() !== regNumber.toLowerCase()) {
                this.updateCarData();
            }
        } else {
            const parsedMileage = parseInt(mileageString);
            const mileage = isNaN(parsedMileage) ? undefined : parsedMileage;

            this.nextStep.emit({
                carDracarData: this.carDracarData,
                carIndicataData: this.carIndicataData,
                makeCode: this.makeCode,
                makeName: this.makeName,
                mileage,
                modelCode: this.modelCode,
                modelName: this.modelName,
                variantCode: this.variantCode,
                regNumber,
            });
        }
    }
    private onGetCarDetailsError(error: HttpErrorResponse) {
        this.loadingCarData = false;
        this.hasError = true;
        this.cdr.markForCheck();

        return throwError(error.error.message);
    }
}
